// We need to register it again because of Vue instance instantiation issues

import type { EventLocationPreference } from '~/components/common/card/TeameventRequestCard.vue'
import { sendViewWishlist } from '~/composables/useGtmEvents'

export interface UiState {
  isCartSidebarOpen: boolean
  isDateRequestSidebarOpen: boolean
  isWaitlistSidebarOpen: boolean
  isNavigationSidebarOpen: boolean
  isFederatedSearchOpen: boolean
  eventRequestOpenedPage: string | undefined
  dateRequestSidebarRequestLocation: string | undefined
  dateRequestSidebarRequestPreferredLocation:
    | EventLocationPreference
    | undefined
  /** When opening the date-request drawer from the buy box, seed the ticket field. */
  dateRequestSuggestedTicketCount: number | undefined
}

export const useUiState = () => {
  if (!import.meta.client) {
    return {
      isCartSidebarOpen: ref(false),
      isDateRequestSidebarOpen: ref(false),
      eventRequestOpenedPage: ref(undefined),
      isWaitlistSidebarOpen: ref(false),
      isNavigationSidebarOpen: ref(false),
      isFederatedSearchOpen: ref(false),
      dateRequestSidebarRequestLocation: '',
      dateRequestSidebarRequestPreferredLocation: ref<
        EventLocationPreference | undefined
      >(undefined),
      dateRequestSuggestedTicketCount: ref<number | undefined>(undefined),
      setDateRequestSuggestedTicketCount: () => {},
      toggleCartSidebar: () => {},
      toggleDateRequestSidebar: () => {},
      toggleWaitlistSidebar: () => {},
      toggleNavigationSidebar: () => {},
      toggleFederatedSearchOpen: () => {},
    }
  }

  const state = useState<UiState>('uiState', () => ({
    isCartSidebarOpen: false,
    isDateRequestSidebarOpen: false,
    eventRequestOpenedPage: undefined,
    isWaitlistSidebarOpen: false,
    isNavigationSidebarOpen: false,
    isFederatedSearchOpen: false,
    dateRequestSidebarRequestLocation: '',
    dateRequestSidebarRequestPreferredLocation: undefined,
    dateRequestSuggestedTicketCount: undefined,
  }))

  // const { sendViewCart, sendDateRequestSidebarView } = useGtm()
  const isCartSidebarOpen = computed(() => state.value.isCartSidebarOpen)

  const toggleCartSidebar = (send_to_gtm: boolean = false) => {
    state.value.isCartSidebarOpen = !state.value.isCartSidebarOpen
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('prevent-scroll')
      if (state.value.isCartSidebarOpen) {
        document.body.classList.add('prevent-scroll')
      }
    }
    if (send_to_gtm) {
      sendViewCart()
    }
  }

  const closeCartSidebar = () => {
    state.value.isCartSidebarOpen = false
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('prevent-scroll')
    }
  }

  const isDateRequestSidebarOpen = computed(
    () => state.value.isDateRequestSidebarOpen
  )
  const toggleDateRequestSidebar = (
    openedPage: string | undefined = undefined,
    requestLocation: string | undefined = undefined,
    wantedLocation: EventLocationPreference | undefined = undefined,
    suggestedTicketCount: number | undefined = undefined
  ) => {
    state.value.isDateRequestSidebarOpen = !state.value.isDateRequestSidebarOpen
    state.value.eventRequestOpenedPage = openedPage
    state.value.dateRequestSidebarRequestLocation = requestLocation
    state.value.dateRequestSidebarRequestPreferredLocation = wantedLocation

    if (state.value.isDateRequestSidebarOpen) {
      state.value.dateRequestSuggestedTicketCount = suggestedTicketCount
    } else {
      state.value.dateRequestSuggestedTicketCount = undefined
    }

    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('prevent-scroll')
      if (state.value.isDateRequestSidebarOpen) {
        document.body.classList.add('prevent-scroll')
      }
    }
    if (state.value.isDateRequestSidebarOpen) {
      sendDateRequestSidebarView(requestLocation || '')
    }
  }
  const eventRequestOpenedPage = computed(
    () => state.value.eventRequestOpenedPage
  )

  const isWaitlistSidebarOpen = computed(
    () => state.value.isWaitlistSidebarOpen
  )
  const toggleWaitlistSidebar = () => {
    state.value.isWaitlistSidebarOpen = !state.value.isWaitlistSidebarOpen

    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('prevent-scroll')
      if (state.value.isWaitlistSidebarOpen) {
        document.body.classList.add('prevent-scroll')
      }
    }
    if (state.value.isWaitlistSidebarOpen) {
      sendWaitlistSidebarView()
    }
  }
  const isNavigationSidebarOpen = computed(
    () => state.value.isNavigationSidebarOpen
  )
  const toggleNavigationSidebar = () => {
    state.value.isNavigationSidebarOpen = !state.value.isNavigationSidebarOpen
  }

  const isFederatedSearchOpen = computed(
    () => state.value.isFederatedSearchOpen
  )
  const toggleFederatedSearchOpen = () => {
    state.value.isFederatedSearchOpen = !state.value.isFederatedSearchOpen
  }

  const dateRequestSidebarRequestLocation = computed(
    () => state.value.dateRequestSidebarRequestLocation
  )
  const dateRequestSidebarRequestPreferredLocation = computed(
    () => state.value.dateRequestSidebarRequestPreferredLocation
  )
  const dateRequestSuggestedTicketCount = computed(
    () => state.value.dateRequestSuggestedTicketCount
  )
  const setDateRequestSuggestedTicketCount = (
    ticketCount: number | undefined
  ) => {
    state.value.dateRequestSuggestedTicketCount = ticketCount
  }

  return {
    isCartSidebarOpen,
    isDateRequestSidebarOpen,
    eventRequestOpenedPage,
    isWaitlistSidebarOpen,
    isNavigationSidebarOpen,
    isFederatedSearchOpen,
    dateRequestSidebarRequestLocation,
    dateRequestSidebarRequestPreferredLocation,
    dateRequestSuggestedTicketCount,
    setDateRequestSuggestedTicketCount,
    toggleCartSidebar,
    toggleDateRequestSidebar,
    toggleWaitlistSidebar,
    toggleNavigationSidebar,
    toggleFederatedSearchOpen,
    closeCartSidebar,
  }
}

export const useSidebarUiState = (
  key: string,
  { gtmCb }: { gtmCb?: (...args: any) => any } = {}
) => {
  if (!import.meta.client) {
    return {
      isOpen: false,
      close: () => {},
      toggle: () => {},
    }
  }

  const isOpen = useState(`singleUiState-${key}`, () => false)

  const toggle = (send_to_gtm: boolean = false) => {
    isOpen.value = !isOpen.value
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('prevent-scroll')
      if (isOpen.value) {
        document.body.classList.add('prevent-scroll')
      }
    }
    if (send_to_gtm && gtmCb) {
      gtmCb()
    }
  }

  const close = () => {
    isOpen.value = false
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('prevent-scroll')
    }
  }

  return {
    isOpen,
    toggle,
    close,
  }
}

export const useWishlistSidebarUiState = () => {
  const { wishlist } = useWishlist()
  return useSidebarUiState('wishlist', {
    gtmCb: () => sendViewWishlist(wishlist.value),
  })
}

export const useActiveOffersSidebarUiState = () => {
  return useSidebarUiState('activeOffers')
}
